import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  CategoryFormDataType,
  CategoryMappingType,
  ClassifierFormDataType,
  CloneFormTableRowButton,
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  filterOption,
  formatCodeName,
  FormDateRangePicker,
  FormField,
  FormSelect,
  FormTableDefaultField,
  getDatePeriodString,
  getFormTableFieldName,
  getValidPeriodDisplayValueWithFormValueAttached,
  RequiredText,
  sortStringLower,
  TanstackFormTable,
} from '@atlas-frontend/sdk';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
  Row,
} from '@tanstack/react-table';
import { Flex } from 'antd';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { FC, memo, useMemo } from 'react';
import { useFormContext, UseFormGetValues, useWatch } from 'react-hook-form';
import translations from './CategoryMappingTable.translations.json';
import { ClassifierMappingActions } from './ClassifierMappingActions';
import { useClassifierMappingContext } from './ClassifierMappingContext';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'category-mapping-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<CategoryMappingType>();

export function CategoryMappingTable() {
  const { classifierFrom, columns, fromOptions, toOptions } =
    useMappingColumns();

  const options = useMemo(
    () => ({
      categoryIdFrom: fromOptions.map((option) => ({
        id: option.value,
        name: option.label,
      })),
      categoryIdTo: toOptions.map((option) => ({
        id: option.value,
        name: option.label,
      })),
    }),
    [fromOptions, toOptions],
  );

  return (
    <Flex vertical gap="middle" align="end" style={{ width: 'fit-content' }}>
      <TanstackFormTable
        name="categoryMappings"
        columns={columns}
        options={options}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
        actions={<ClassifierMappingActions classifier={classifierFrom} />}
      />
    </Flex>
  );
}

const CategoryTypePreview: FC<{
  name: string;
  optionMapping: Map<number | null, CategoryFormDataType>;
}> = ({ name, optionMapping }) => {
  const id = useWatch({
    name,
  });
  const { t } = useTranslation();
  const categoryType = optionMapping.get(id)?.categoryType;
  return id ? t('categoryType.' + categoryType) : null;
};

function useMappingColumns() {
  const { classifierFrom, classifierTo } = useClassifierMappingContext();
  const { getValues } = useFormContext();

  const { columns, fromOptions, toOptions } = useMemo(
    () => getColumns(classifierFrom, classifierTo, getValues),
    [classifierFrom, classifierTo, getValues],
  );

  return { classifierFrom, columns, fromOptions, toOptions };
}

function getColumns(
  classifierFrom: ClassifierFormDataType | undefined,
  classifierTo: ClassifierFormDataType | undefined,
  getValues: UseFormGetValues<any>,
) {
  const { options: fromOptions, optionMapping: fromOptionMapping } =
    createOptions(classifierFrom);
  const { options: toOptions, optionMapping: toOptionMapping } =
    createOptions(classifierTo);
  const optionMapping = new Map(
    (function* () {
      yield* fromOptionMapping;
      yield* toOptionMapping;
    })(),
  );

  const columns: ColumnDef<CategoryMappingType, any>[] = [
    columnHelper.accessor((rowData) => rowData.categoryIdFrom, {
      id: 'categoryIdFrom',
      header: () => <RequiredText>{t('field.category1.label')}</RequiredText>,
      cell: (props) => (
        <CategorySelectCell
          {...props}
          optionMapping={optionMapping}
          options={fromOptions}
        />
      ),
      filterFn: 'includesOption',
      sortingFn: (a, b) =>
        categorySortFn(a, b, 'categoryIdFrom', optionMapping),
      meta: {
        width: '30%',
        minWidth: 150,
      },
    }),
    columnHelper.accessor('id', {
      header: t('field.category1.type'),
      cell: (props) => (
        <CategoryTypeCell
          {...props}
          prop="categoryIdFrom"
          optionMapping={optionMapping}
        />
      ),
      enableColumnFilter: false,
      enableSorting: false,
      meta: {
        width: 100,
      },
    }),
    columnHelper.accessor((rowData) => rowData.categoryIdTo, {
      id: 'categoryIdTo',
      header: () => <RequiredText>{t('field.category2.label')}</RequiredText>,
      cell: (props) => (
        <CategorySelectCell
          {...props}
          optionMapping={optionMapping}
          options={toOptions}
        />
      ),
      filterFn: 'includesOption',
      sortingFn: (a, b) => categorySortFn(a, b, 'categoryIdTo', optionMapping),
      meta: {
        width: '30%',
        minWidth: 150,
      },
    }),
    columnHelper.accessor('id', {
      header: t('field.category2.type'),
      cell: (props) => (
        <CategoryTypeCell
          {...props}
          prop="categoryIdTo"
          optionMapping={optionMapping}
        />
      ),
      enableColumnFilter: false,
      enableSorting: false,
      meta: {
        width: 100,
      },
    }),
    columnHelper.accessor('description', {
      header: t('field.description.label'),
      cell: FormTableDefaultField,
      meta: {
        width: '40%',
        minWidth: 150,
      },
      sortingFn: 'localeCompareCaseSensitive',
    }),
    columnHelper.accessor(
      (_rowData, index) =>
        getValidPeriodDisplayValueWithFormValueAttached(
          getValues(`categoryMappings.${index}.validPeriod`),
        ),
      {
        id: 'validPeriod',
        header: t('field.validityPeriod.label'),
        cell: ValidPeriodCell,
        meta: {
          width: 266,
        },
        filterFn: 'validPeriod',
      },
    ),
    columnHelper.display({
      id: 'operations',
      header: () => (
        <Flex justify="center" gap="small">
          <CreateFormTableRowButton autoFocusField="categoryIdFrom" />
        </Flex>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: (props) => (
        <Flex justify="center" gap="small">
          <CloneFormTableRowButton
            context={props}
            resetId
            autoFocusField="categoryIdFrom"
          />
          <DeleteFormTableRowButton context={props} />
        </Flex>
      ),
      meta: {
        width: 88,
        className: 'sticky-right',
      },
    }),
  ];

  return { columns, fromOptions, toOptions };
}

function categoryOptions(categories?: CategoryFormDataType[]) {
  return (
    categories
      ?.map((category) => ({
        category,
        label: `${category.code} - ${category.name}`,
        value: category.id,
      }))
      .filter(
        (
          option,
        ): option is {
          category: CategoryFormDataType;
          label: string;
          value: number;
        } => option.value != null,
      ) ?? []
  );
}

function categoryName(
  categoryId: number,
  optionMapping: Map<number, CategoryFormDataType>,
) {
  const category = optionMapping.get(categoryId);
  return category ? `${category.code} - ${category.name}` : null;
}

function categorySortFn(
  a: Row<CategoryMappingType>,
  b: Row<CategoryMappingType>,
  prop: 'categoryIdFrom' | 'categoryIdTo',
  optionMapping: Map<number, CategoryFormDataType>,
) {
  return sortStringLower(
    categoryName(a.original[prop], optionMapping),
    categoryName(b.original[prop], optionMapping),
  );
}

function createOptions(classifier?: ClassifierFormDataType) {
  const options = categoryOptions(classifier?.categories)
    .concat(categoryOptions(classifier?.categoryGroups))
    .concat(categoryOptions(classifier?.combinedGroups));
  options.sort((a, b) => sortStringLower(a.label, b.label));

  const optionMapping = new Map(
    options.map((option) => [option.value, option.category]),
  );
  return {
    options,
    optionMapping,
  };
}

interface CategoryOption extends DefaultOptionType {
  category: CategoryFormDataType;
}

const CategorySelectCell: FC<
  CellContext<CategoryMappingType, any> & {
    optionMapping: Map<number, CategoryFormDataType>;
    options: CategoryOption[];
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField
      name={name}
      size="small"
      preview={{
        render: (value) => (
          <CategorySelectPreview
            options={props.optionMapping}
            selectedValue={value}
          />
        ),
      }}
    >
      <FormSelect
        allowClear={true}
        autoFocus
        className="category-select"
        size="small"
        defaultOpen
        filterOption={filterOption}
        name={name}
        options={props.options}
        showSearch
      />
    </FormField>
  );
});

const CategoryTypeCell: FC<
  CellContext<CategoryMappingType, any> & {
    prop: string;
    optionMapping: Map<number, CategoryFormDataType>;
  }
> = memo((props) => {
  const name = `categoryMappings.${props.row.index}.${props.prop}`;
  return (
    <FormField
      name={name.replace('Id', 'Type')}
      size="small"
      preview={{
        previewOnly: true,
        render: () => (
          <CategoryTypePreview
            name={name}
            optionMapping={props.optionMapping}
          />
        ),
      }}
    />
  );
});

const ValidPeriodCell: FC<CellContext<CategoryMappingType, any>> = memo(
  (props) => {
    const name = getFormTableFieldName(props);
    return (
      <FormField
        name={name}
        size="small"
        preview={{
          render: (value) => getDatePeriodString(value?.[0], value?.[1]),
        }}
      >
        <FormDateRangePicker
          size="small"
          allowEmpty={[true, true]}
          autoFocus
          name={name}
        />
      </FormField>
    );
  },
);

interface CategorySelectPreviewProps {
  selectedValue: number | undefined;
  options: Map<number, CategoryFormDataType>;
}

const CategorySelectPreview: FC<CategorySelectPreviewProps> = (props) => {
  const { options, selectedValue } = props;

  return useMemo(() => {
    const val = options.get(selectedValue ?? 0);
    if (val) {
      return formatCodeName(val);
    }
    return '';
  }, [options, selectedValue]);
};
